// ============================================================================
// Chemicals recorded at a building — the ONE save path, shared.
//
// 2026-09-09, facility-chemicals-in-app. Until today the only code that had
// ever written a chemical lived inside the AM Collect review screen
// (promoteChemicals). The survey that fed it is dead, so nobody could record a
// chemical anywhere. Ben: "We are now in the DO IT IN THE APP phase."
//
// The read-diff-insert-restore-remove work moved here rather than being forked:
// the facility card and the review screen call the same functions, and the
// review screen keeps working against the submissions it already holds.
//
// Two things this fixes on the way past:
//   * Removing a chemical and adding it back used to raise 23505. Uniqueness
//     ignored the removed flag, and the save path inserted a second row instead
//     of restoring the one that was there. It now restores, so the row keeps
//     its id and its audit trail.
//   * A row can now be a typed product name with no catalogue match, which is
//     how anything not on the list gets recorded.
//
// ⛔ Every write goes through commitWrite: a PostgREST write refused by RLS
// returns zero rows and NO error (TCSD-0168), and a chemical that silently
// failed to save is how the last five months of nothing got believed.
// ============================================================================

import { supabase } from '../supabase';
import { commitWrite } from '../save-path';

/** Who supplies the chemical at this building. NULL means nobody has said. */
export type ChemicalManagedBy = 'TCS' | 'Facility';

/** facility_chemicals.source — the allowed list already covers a card write. */
export type ChemicalSource = 'AM_Collect' | 'Manual' | 'Inspection';

/** The word for each answer, written down once. NULL is a real answer. */
export function managedByLabel(v: ChemicalManagedBy | null | undefined): string {
  if (v === 'TCS') return 'We supply it';
  if (v === 'Facility') return 'The facility supplies it';
  return 'Not known';
}

export const MANAGED_BY_CHOICES: readonly {
  value: ChemicalManagedBy | '';
  label: string;
}[] = [
  { value: '', label: 'Not known' },
  { value: 'TCS', label: 'We supply it' },
  { value: 'Facility', label: 'The facility supplies it' },
];

export interface FacilityChemical {
  fc_id: string;
  facility_id: string;
  /** Catalogue item, or null when this is a typed name waiting for a decision. */
  supply_id: string | null;
  /** What somebody typed, when it was not on the list. */
  supply_name: string | null;
  catalogue_name: string | null;
  catalogue_is_active: boolean | null;
  /** Set when the row was recorded against a duplicate that now points elsewhere. */
  merged_into_supply_id: string | null;
  /** Catalogue name when there is one, otherwise the typed name. */
  display_name: string;
  unmatched: boolean;
  managed_by: ChemicalManagedBy | null;
  notes: string | null;
  source: ChemicalSource;
  recorded_by_eid: string | null;
  recorded_at: string | null;
}

const ROW_COLUMNS = `fc_id, facility_id, supply_id, supply_name, managed_by, notes,
  source, is_deleted, created_at, last_confirmed_at, last_confirmed_by_eid, confirmed_by,
  supplies ( item_name, supply_category, is_active, merged_into_supply_id )`;

interface RawRow {
  fc_id: string;
  facility_id: string;
  supply_id: string | null;
  supply_name: string | null;
  managed_by: string | null;
  notes: string | null;
  source: string;
  is_deleted: boolean;
  created_at: string | null;
  last_confirmed_at: string | null;
  last_confirmed_by_eid: string | null;
  confirmed_by: string | null;
  supplies?: {
    item_name: string | null;
    supply_category: string | null;
    is_active: boolean | null;
    merged_into_supply_id: string | null;
  } | null;
}

function toChemical(r: RawRow): FacilityChemical {
  const catalogueName = r.supplies?.item_name ?? null;
  const typed = r.supply_name ?? null;
  return {
    fc_id: r.fc_id,
    facility_id: r.facility_id,
    supply_id: r.supply_id ?? null,
    supply_name: typed,
    catalogue_name: catalogueName,
    catalogue_is_active: r.supplies?.is_active ?? null,
    merged_into_supply_id: r.supplies?.merged_into_supply_id ?? null,
    // The catalogue name wins; the typed name is the answer when there is no
    // catalogue row. The id is a last resort for a read that lost its embed —
    // it is not a name and a person should never be shown it as one.
    display_name: catalogueName ?? typed ?? r.supply_id ?? r.fc_id,
    unmatched: r.supply_id == null,
    managed_by: (r.managed_by as ChemicalManagedBy | null) ?? null,
    notes: r.notes ?? null,
    source: r.source as ChemicalSource,
    recorded_by_eid: r.last_confirmed_by_eid ?? r.confirmed_by ?? null,
    recorded_at: r.last_confirmed_at ?? r.created_at ?? null,
  };
}

/**
 * Everything recorded at this building right now.
 *
 * ⛔ Reads `error` and throws. An empty list is not an empty answer: a refused
 * read would otherwise render as "no chemicals on site" for a real building.
 */
export async function fetchFacilityChemicals(
  facilityId: string,
): Promise<FacilityChemical[]> {
  const { data, error } = await supabase
    .from('facility_chemicals')
    .select(ROW_COLUMNS)
    .eq('facility_id', facilityId)
    .eq('is_deleted', false);
  if (error) throw error;
  return ((data ?? []) as unknown as RawRow[])
    .map(toChemical)
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
}

/** Resolve recorder ids to names. v_am_name is a resolver, never a picker. */
export async function fetchRecorderNames(
  eids: readonly string[],
): Promise<Record<string, string>> {
  const wanted = Array.from(new Set(eids.filter(Boolean)));
  if (wanted.length === 0) return {};
  const { data, error } = await supabase
    .from('v_am_name')
    .select('employee_id, display_name')
    .in('employee_id', wanted);
  if (error) throw error;
  const out: Record<string, string> = {};
  for (const r of (data ?? []) as { employee_id: string; display_name: string | null }[]) {
    if (r.display_name) out[r.employee_id] = r.display_name;
  }
  return out;
}

export interface ExistingRef {
  fc_id: string;
  supply_id: string | null;
  supply_name: string | null;
  is_deleted: boolean;
}

/** Every row for a building INCLUDING removed ones — the diff needs both. */
async function fetchRefs(facilityId: string): Promise<ExistingRef[]> {
  const { data, error } = await supabase
    .from('facility_chemicals')
    .select('fc_id, supply_id, supply_name, is_deleted')
    .eq('facility_id', facilityId);
  if (error) throw error;
  return (data ?? []) as ExistingRef[];
}

function sameTypedName(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export interface AddChemicalArgs {
  facilityId: string;
  /** One of these two, never both — the database refuses the other shapes. */
  supplyId?: string | null;
  typedName?: string | null;
  managedBy?: ChemicalManagedBy | null;
  notes?: string | null;
  byEid: string | null;
  source?: ChemicalSource;
}

/** Thrown when the chemical is already on the building and still live. */
export class ChemicalAlreadyOnBuilding extends Error {}

/**
 * Record a chemical at a building.
 *
 * If the same chemical was removed before, the ORIGINAL row is restored rather
 * than a second one inserted — that is the 23505 fix, and it keeps one history
 * per chemical per building instead of a pile of rows.
 */
export async function addFacilityChemical(args: AddChemicalArgs): Promise<string> {
  const supplyId = args.supplyId?.trim() || null;
  const typedName = args.typedName?.trim() || null;
  if ((supplyId && typedName) || (!supplyId && !typedName)) {
    throw new Error('A chemical is either a catalogue item or a typed name, not both and not neither.');
  }

  const refs = await fetchRefs(args.facilityId);
  const match = refs.find((r) =>
    supplyId ? r.supply_id === supplyId : sameTypedName(r.supply_name, typedName),
  );

  const stamp = new Date().toISOString();

  if (match && !match.is_deleted) {
    throw new ChemicalAlreadyOnBuilding(
      'That chemical is already recorded at this building.',
    );
  }

  if (match && match.is_deleted) {
    const rows = await commitWrite<{ fc_id: string }>({
      label: 'put that chemical back on this building',
      write: supabase
        .from('facility_chemicals')
        .update({
          is_deleted: false,
          deleted_at: null,
          managed_by: args.managedBy ?? null,
          notes: args.notes?.trim() || null,
          last_confirmed_at: stamp,
          last_confirmed_by_eid: args.byEid,
        })
        .eq('fc_id', match.fc_id)
        .select('fc_id'),
      touches: ['facility_chemicals'],
    });
    return rows[0]?.fc_id ?? match.fc_id;
  }

  const inserted = await commitWrite<{ fc_id: string }>({
    label: 'record that chemical at this building',
    write: supabase
      .from('facility_chemicals')
      .insert({
        facility_id: args.facilityId,
        supply_id: supplyId,
        supply_name: typedName,
        managed_by: args.managedBy ?? null,
        notes: args.notes?.trim() || null,
        source: args.source ?? 'Manual',
        last_confirmed_at: stamp,
        last_confirmed_by_eid: args.byEid,
      })
      .select('fc_id'),
    touches: ['facility_chemicals'],
  });
  return inserted[0]!.fc_id;
}

export interface UpdateChemicalArgs {
  fcId: string;
  managedBy?: ChemicalManagedBy | null;
  notes?: string | null;
  /** Only meaningful on an unmatched row. */
  typedName?: string | null;
  byEid: string | null;
}

export async function updateFacilityChemical(args: UpdateChemicalArgs): Promise<void> {
  const patch: Record<string, unknown> = {
    last_confirmed_at: new Date().toISOString(),
    last_confirmed_by_eid: args.byEid,
  };
  // ⛔ Only send what the caller actually edited. An unconditional field: null
  // is a writer that has already decided the column's meaning (TCSD-0072).
  if ('managedBy' in args) patch.managed_by = args.managedBy ?? null;
  if ('notes' in args) patch.notes = args.notes?.trim() || null;
  if ('typedName' in args) {
    const typed = args.typedName?.trim() || null;
    if (!typed) throw new Error('A typed-in chemical still needs a name.');
    patch.supply_name = typed;
  }

  await commitWrite({
    label: 'save that change to the chemical',
    write: supabase
      .from('facility_chemicals')
      .update(patch)
      .eq('fc_id', args.fcId)
      .select('fc_id'),
    touches: ['facility_chemicals'],
  });
}

/**
 * Take a chemical off a building. Soft delete — the row and its audit trail
 * stay, a hard DELETE is blocked on this table anyway, and adding it back
 * restores this same row.
 */
export async function removeFacilityChemical(fcId: string): Promise<void> {
  await commitWrite({
    label: 'take that chemical off this building',
    write: supabase
      .from('facility_chemicals')
      .update({ is_deleted: true, deleted_at: new Date().toISOString() })
      .eq('fc_id', fcId)
      .select('fc_id'),
    touches: ['facility_chemicals'],
  });
}

export interface ReconcileArgs {
  facilityId: string;
  /** The complete list of CATALOGUE items that should be live afterwards. */
  supplyIds: readonly string[];
  source?: ChemicalSource;
  byEid: string | null;
}

export interface ReconcileResult {
  added: string[];
  restored: string[];
  removed: string[];
}

export interface ReconcilePlan {
  /** Catalogue ids whose removed row should be put back. */
  toRestore: string[];
  /** Catalogue ids with no row at all yet. */
  toInsert: string[];
  /** fc_ids of live rows that should come off. */
  toRemove: string[];
  /** supply_id -> fc_id of the removed row to restore. */
  removedIndex: Map<string, string>;
}

/**
 * The whole diff, pure and testable — no network, no clock.
 *
 * ⛔ Rows with no catalogue id are dropped from consideration on the FIRST line.
 * A survey promotion carries catalogue ids only, so a typed-in name is not
 * "missing from the list" — it is a different kind of record, and sweeping it
 * would delete exactly the entries somebody took the trouble to type.
 */
export function planChemicalReconcile(
  refs: readonly ExistingRef[],
  supplyIds: readonly string[],
): ReconcilePlan {
  const wanted = Array.from(new Set(supplyIds.filter(Boolean)));
  const catalogueRefs = refs.filter((r) => r.supply_id !== null);

  const live = new Map<string, string>();
  const removedIndex = new Map<string, string>();
  for (const r of catalogueRefs) {
    const key = r.supply_id as string;
    if (r.is_deleted) {
      if (!removedIndex.has(key)) removedIndex.set(key, r.fc_id);
    } else {
      live.set(key, r.fc_id);
    }
  }

  return {
    toRestore: wanted.filter((id) => !live.has(id) && removedIndex.has(id)),
    toInsert: wanted.filter((id) => !live.has(id) && !removedIndex.has(id)),
    toRemove: Array.from(live.entries())
      .filter(([supplyId]) => !wanted.includes(supplyId))
      .map(([, fcId]) => fcId),
    removedIndex,
  };
}

/**
 * Make the building's catalogue chemicals match `supplyIds` exactly.
 *
 * This is the part promoteChemicals used to own. Two rules that are easy to get
 * wrong and are asserted by the tests:
 *   1. A chemical that was removed and is wanted again is RESTORED, not
 *      re-inserted.
 *   2. ⛔ Typed-name rows are never touched. They carry no catalogue id, so a
 *      survey promotion has nothing to say about them — sweeping them would
 *      delete exactly the entries somebody bothered to type.
 */
export async function reconcileFacilityChemicals(
  args: ReconcileArgs,
): Promise<ReconcileResult> {
  const refs = await fetchRefs(args.facilityId);
  const plan = planChemicalReconcile(refs, args.supplyIds);
  const { toRestore, toInsert, toRemove } = plan;
  const removed = plan.removedIndex;

  const stamp = new Date().toISOString();

  if (toRestore.length > 0) {
    const ids = toRestore.map((id) => removed.get(id)!);
    await commitWrite({
      label: 'put those chemicals back on this building',
      write: supabase
        .from('facility_chemicals')
        .update({
          is_deleted: false,
          deleted_at: null,
          last_confirmed_at: stamp,
          last_confirmed_by_eid: args.byEid,
        })
        .in('fc_id', ids)
        .select('fc_id'),
      touches: ['facility_chemicals'],
      expect: 'at-least-one',
    });
  }

  if (toInsert.length > 0) {
    await commitWrite({
      label: 'record those chemicals at this building',
      write: supabase
        .from('facility_chemicals')
        .insert(
          toInsert.map((supply_id) => ({
            facility_id: args.facilityId,
            supply_id,
            source: args.source ?? 'AM_Collect',
            confirmed_by: args.byEid,
            confirmed_at: stamp,
            last_confirmed_at: stamp,
            last_confirmed_by_eid: args.byEid,
          })),
        )
        .select('fc_id'),
      touches: ['facility_chemicals'],
      expect: 'at-least-one',
    });
  }

  if (toRemove.length > 0) {
    await commitWrite({
      label: 'take those chemicals off this building',
      write: supabase
        .from('facility_chemicals')
        .update({ is_deleted: true, deleted_at: stamp })
        .in('fc_id', toRemove)
        .select('fc_id'),
      touches: ['facility_chemicals'],
      expect: 'at-least-one',
    });
  }

  return { added: toInsert, restored: toRestore, removed: toRemove };
}

// ---------------------------------------------------------------------------
// Typed-in names waiting for a catalogue decision.
//
// This is the mechanism that keeps the catalogue current: somebody types a
// product that is not on the list, it shows up here grouped by name, and Ben
// decides whether it deserves a catalogue entry. ⛔ No automation, no
// auto-matching, no fuzzy guessing — and pg_trgm is not installed anyway.
// ---------------------------------------------------------------------------

export interface UnmatchedChemicalUse {
  fc_id: string;
  facility_id: string;
  facility_name: string | null;
  managed_by: ChemicalManagedBy | null;
  notes: string | null;
  typed_name: string;
}

export interface UnmatchedChemicalGroup {
  /** The name as it was typed, first occurrence alphabetically. */
  name: string;
  buildings: UnmatchedChemicalUse[];
}

/** Group typed-in names case-insensitively, without pretending to match them. */
export function groupUnmatched(
  uses: readonly UnmatchedChemicalUse[],
): UnmatchedChemicalGroup[] {
  const byKey = new Map<string, UnmatchedChemicalGroup>();
  for (const u of uses) {
    const key = u.typed_name.trim().toLowerCase();
    const existing = byKey.get(key);
    if (existing) {
      existing.buildings.push(u);
    } else {
      byKey.set(key, { name: u.typed_name.trim(), buildings: [u] });
    }
  }
  return Array.from(byKey.values()).sort(
    (a, b) =>
      b.buildings.length - a.buildings.length || a.name.localeCompare(b.name),
  );
}

export async function fetchUnmatchedChemicals(): Promise<UnmatchedChemicalGroup[]> {
  const { data, error } = await supabase
    .from('facility_chemicals')
    .select('fc_id, facility_id, supply_name, managed_by, notes, facilities ( facility_name )')
    .is('supply_id', null)
    .eq('is_deleted', false);
  if (error) throw error;

  const uses: UnmatchedChemicalUse[] = ((data ?? []) as unknown as {
    fc_id: string;
    facility_id: string;
    supply_name: string | null;
    managed_by: string | null;
    notes: string | null;
    facilities?: { facility_name: string | null } | null;
  }[])
    .filter((r) => !!r.supply_name)
    .map((r) => ({
      fc_id: r.fc_id,
      facility_id: r.facility_id,
      facility_name: r.facilities?.facility_name ?? null,
      managed_by: (r.managed_by as ChemicalManagedBy | null) ?? null,
      notes: r.notes ?? null,
      typed_name: r.supply_name as string,
    }));

  return groupUnmatched(uses);
}

// ---------------------------------------------------------------------------
// Which clinic uses which chemical — the portfolio read.
//
// ⛔ Every row is resolved THROUGH THE POINTER. A building whose chemical was
// recorded against a duplicate import still counts toward the product that
// survives, which is the whole point of keeping the duplicate rather than
// deleting it.
// ---------------------------------------------------------------------------

export interface PortfolioRow {
  fc_id: string;
  facility_id: string;
  facility_name: string | null;
  market_region: string | null;
  facility_status: string | null;
  managed_by: ChemicalManagedBy | null;
  notes: string | null;
  /** What was recorded. */
  recorded_supply_id: string | null;
  /** What it resolves to once the pointer is followed. */
  effective_supply_id: string | null;
  effective_name: string;
  resolved_through_pointer: boolean;
  unmatched: boolean;
}

export interface ChemicalPortfolio {
  rows: PortfolioRow[];
  activeFacilities: number;
  activeFacilitiesWithSomething: number;
  activeFacilitiesWithNothing: number;
  markets: string[];
}

export async function fetchChemicalPortfolio(): Promise<ChemicalPortfolio> {
  const [chemRes, catRes, facRes] = await Promise.all([
    supabase
      .from('facility_chemicals')
      .select(
        `fc_id, facility_id, supply_id, supply_name, managed_by, notes,
         facilities ( facility_name, market_region, status ),
         supplies ( item_name, is_active, merged_into_supply_id )`,
      )
      .eq('is_deleted', false),
    supabase.from('supplies').select('supply_id, item_name'),
    supabase.from('facilities').select('facility_id, status').eq('status', 'Active'),
  ]);
  // ⛔ Read every error. An empty list here would read as "nobody uses
  // anything", which is a lie a coverage number must never tell.
  if (chemRes.error) throw chemRes.error;
  if (catRes.error) throw catRes.error;
  if (facRes.error) throw facRes.error;

  const catalogueName = new Map<string, string>();
  for (const s of (catRes.data ?? []) as { supply_id: string; item_name: string | null }[]) {
    catalogueName.set(s.supply_id, s.item_name ?? s.supply_id);
  }

  const rows: PortfolioRow[] = ((chemRes.data ?? []) as unknown as {
    fc_id: string;
    facility_id: string;
    supply_id: string | null;
    supply_name: string | null;
    managed_by: string | null;
    notes: string | null;
    facilities?: { facility_name: string | null; market_region: string | null; status: string | null } | null;
    supplies?: { item_name: string | null; is_active: boolean | null; merged_into_supply_id: string | null } | null;
  }[]).map((r) => {
    const pointer = r.supplies?.merged_into_supply_id ?? null;
    const effectiveId = r.supply_id ? pointer ?? r.supply_id : null;
    const effectiveName = effectiveId
      ? catalogueName.get(effectiveId) ?? effectiveId
      : (r.supply_name ?? r.fc_id);
    return {
      fc_id: r.fc_id,
      facility_id: r.facility_id,
      facility_name: r.facilities?.facility_name ?? null,
      market_region: r.facilities?.market_region ?? null,
      facility_status: r.facilities?.status ?? null,
      managed_by: (r.managed_by as ChemicalManagedBy | null) ?? null,
      notes: r.notes ?? null,
      recorded_supply_id: r.supply_id ?? null,
      effective_supply_id: effectiveId,
      effective_name: effectiveName,
      resolved_through_pointer: !!pointer,
      unmatched: r.supply_id == null,
    };
  });

  const activeIds = new Set(
    ((facRes.data ?? []) as { facility_id: string }[]).map((f) => f.facility_id),
  );
  const activeWith = new Set(
    rows.filter((r) => activeIds.has(r.facility_id)).map((r) => r.facility_id),
  );

  return {
    rows,
    activeFacilities: activeIds.size,
    activeFacilitiesWithSomething: activeWith.size,
    activeFacilitiesWithNothing: activeIds.size - activeWith.size,
    markets: Array.from(
      new Set(rows.map((r) => r.market_region).filter((m): m is string => !!m)),
    ).sort(),
  };
}

export interface ByChemicalGroup {
  key: string;
  name: string;
  unmatched: boolean;
  rows: PortfolioRow[];
  buildings: number;
}

/** Group by the product a row RESOLVES to, not the one it was recorded against. */
export function groupByChemical(rows: readonly PortfolioRow[]): ByChemicalGroup[] {
  const byKey = new Map<string, ByChemicalGroup>();
  for (const r of rows) {
    const key = r.effective_supply_id ?? `typed:${r.effective_name.trim().toLowerCase()}`;
    const g = byKey.get(key);
    if (g) {
      g.rows.push(r);
    } else {
      byKey.set(key, {
        key,
        name: r.effective_name,
        unmatched: r.unmatched,
        rows: [r],
        buildings: 0,
      });
    }
  }
  const out = Array.from(byKey.values());
  for (const g of out) g.buildings = new Set(g.rows.map((r) => r.facility_id)).size;
  return out.sort((a, b) => b.buildings - a.buildings || a.name.localeCompare(b.name));
}

export interface ByBuildingGroup {
  facility_id: string;
  facility_name: string;
  market_region: string | null;
  rows: PortfolioRow[];
}

export function groupByBuilding(rows: readonly PortfolioRow[]): ByBuildingGroup[] {
  const byKey = new Map<string, ByBuildingGroup>();
  for (const r of rows) {
    const g = byKey.get(r.facility_id);
    if (g) {
      g.rows.push(r);
    } else {
      byKey.set(r.facility_id, {
        facility_id: r.facility_id,
        facility_name: r.facility_name ?? r.facility_id,
        market_region: r.market_region,
        rows: [r],
      });
    }
  }
  return Array.from(byKey.values()).sort((a, b) =>
    a.facility_name.localeCompare(b.facility_name),
  );
}
